<template>
  <div>
    <div class='flex flex-wrap flex-col sm:flex-row justify-between sm:items-center gap-4 max-w-[80rem]'>
      <div>
        <h2 class='text-jananavy-900 font-medium'><slot name='title' /></h2>
        <p class='font-light text-sm'><slot name='description' /></p>
      </div>
      <div class='flex gap-2' v-if='props.showFilter'>
        <SelectInput :options='viewOptions' v-model='state.view' label='View' :srLabel='true' />
      </div>
    </div>
    <LoadingLayout :isLoading='isLoading' class='mt-8' :slownessThreshold='0'>
      <ReportNoDataWarning :warn='!view' :limitWidth='true'>
        <GraphStatCards :stats='view.stats' :isPrint='props.isPrint' />

        <div class='mt-8' :class='!props.isPrint ? "overflow-auto" : ""'>
          <table class='min-w-full 2xl:min-w-[80rem] rounded-md force-rounded'>
            <thead class='bg-jananavy-900 text-white'>
              <tr>
                <th class='text-left font-medium px-4 py-2 min-w-36 sticky left-0 bg-jananavy-900'>Objective</th>
                <th class='text-left font-medium px-4 py-2' v-if='props.showOrganisation'>Organisation</th>
                <th class='text-left font-medium px-4 py-2' v-for='entity in view.entities'>
                  <strong class='font-medium'>{{ entity.header.asset.name }}</strong><br />
                  <span class='font-light text-sm' v-if='props.showAssetManager'>{{ entity.header.assetManager.name }}</span>
                </th>
              </tr>
            </thead>
            <tbody class='divide-y border-b'>
              <template v-for='theme in view.themes'>
                <template v-if='state.view === "all" || state.view === theme.header.id'>
                  <tr class='bg-janaturq-400'>
                    <th class='px-4 py-2 text-left font-medium bg-janaturq-400 text-jananavy-900 text-sm sticky left-0'>{{ theme.header.name }}</th>
                    <th v-for='i in (view.entities.length + props.showOrganisation)' class='bg-janaturq-400'></th>
                  </tr>
                  <template v-for='objective in theme.objectives' :key='objective.header.id'>
                    <template v-for='organisation in objective.organisations' :key='organisation.header.id'>
                      <tr>
                        <td class='px-4 py-2 text-sm font-light bg-janaturq-100 sticky left-0'>
                          {{ objective.header.name }}
                        </td>
                        <td class='px-4 py-2 text-sm font-light bg-janaturq-100' v-if='props.showOrganisation'>
                          {{ organisation.header.name }}
                        </td>
                        <td class='px-4 py-2 text-sm font-light' v-for='entity in view.entities'>
                          <div class='flex items-center gap-1' v-if='organisation.entities[entity.header.id]'>
                            <span class='rounded-sm w-6 h-6 inline-block' :class='scoreMap.getColour(organisation.entities[entity.header.id].score, "bg", "high")' />
                            <span>{{ scoreMap.getLabel(organisation.entities[entity.header.id].score) }}</span>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </template>
                </template>
              </template>
            </tbody>
          </table>
        </div>
      </ReportNoDataWarning>
    </LoadingLayout>
  </div>
</template>

<script setup>

import { computed, watch, reactive } from 'vue';

import SelectInput from '@/components/inputs/select-input.vue';

import LoadingLayout from '@/components/layouts/loading-layout.vue';

import ReportNoDataWarning from '@/components/report/report-no-data-warning.vue';

import GraphStatCards from '@/components/reports/graphs/graph-stat-cards.vue';

import { usePayloadRequest } from '@/kernel/api';
import { scoreMap } from '@/kernel/scoring';

const props = defineProps({
  params: Object,
  showOrganisation: Boolean,
  showAssetManager: Boolean,
  view: String,
  showFilter: Boolean,
  isPrint: Boolean
});

const state = reactive({
  view: 'all'
});

const viewOptions = computed(() => {
  const options = [
    {label: 'All Themes', value: 'all'}
  ];

  const data = request.getData();

  if (!data?.docs?.length) {
    return options;
  }

  const themes = {};

  data.docs.forEach((doc) => {
    const themeId = doc.theme.id;

    if (!themes[themeId]) {
      themes[themeId] = doc.theme;
    }
  });

  return [
    ...options,
    ...Object.values(themes).map(theme => ({label: theme.name, value: theme.id}))
  ];
});

const request = usePayloadRequest('GET', '/assessment-response/historical-matrix');

const isLoading = computed(() => {
  return request.isLoading() || request.getData() === null;
});

const view = computed(() => {
  const data = request.getData();

  if (!data?.docs?.length) {
    return null;
  }

  let themes = {};
  let entities = {};

  data.docs.forEach((doc) => {
    const themeId = doc.theme.id;
    const objectiveId = doc.objective.id;
    const organisationId = doc.organisation.id;

    const entity = getEntity(doc);
    const entityId = entity.id;

    if (!themes[themeId]) {
      themes[themeId] = {
        header: doc.theme,
        objectives: {}
      }
    }

    const theme = themes[themeId];

    if (!theme.objectives[objectiveId]) {
      theme.objectives[objectiveId] = {
        header: doc.objective,
        organisations: {}
      }
    }

    const objective = theme.objectives[objectiveId];

    if (!objective.organisations[organisationId]) {
      objective.organisations[organisationId] = {
        header: doc.organisation,
        entities: {}
      }
    }

    const organisation = objective.organisations[organisationId];

    if (!organisation.entities[entityId]) {
      organisation.entities[entityId] = {
        score: doc.score
      }
    }

    if (!entities[entityId]) {
      entities[entityId] = {
        header: entity
      }
    }
  });

  Object.values(themes).forEach((theme) => {
    theme.objectives = Object.values(theme.objectives);

    theme.objectives.forEach((objective) => {
      objective.organisations = Object.values(objective.organisations);
    });
  });

  themes = Object.values(themes);
  entities = Object.values(entities);

  const stats = [
    {name: 'Engagements', value: `${data.supplemental.stats.answeredEngagements}/${data.supplemental.stats.allEngagements}`},
    {name: 'Objectives', value: data.supplemental.stats.objectiveCode}
  ];

  if (props.view === 'platform') {
    stats.push(
      {name: 'Assets', value: data.supplemental.stats.platformAsset},
      {name: 'Asset Managers', value: data.supplemental.stats.platformAssetManager}
    );
  } else {
    stats.push(
      {name: 'Assets', value: data.supplemental.stats.organisationAsset},
      {name: 'Asset Managers', value: data.supplemental.stats.organisationAssetManager}
    );
  }

  return {
    themes,
    entities,
    stats
  }
});

const getEntity = (doc) => {
  if (props.view === 'organisation') {
    return {
      id: `${doc.organisationAsset.id}.${doc.organisationAssetManager?.id || 'null'}`,
      asset: doc.organisationAsset,
      assetManager: doc.organisationAssetManager
    }
  }

  return {
    id: `${doc.platformAsset.id}.${doc.platformAssetManager?.id || 'null'}`,
    asset: doc.platformAsset,
    assetManager: doc.platformAssetManager
  }
}

const load = () => {
  if (!props.params) {
    return;
  }

  request.doRequest({
    params: {
      ...props.params
    }
  });
}

watch(() => props.params, () => {
  load();
});

load();

</script>
